"use client";

// ToastProvider — row #137 α full alert sweep.
//
// React Context + queue state for transient toasts. Mounted at app/layout.js
// root parallel to DialogProvider (matches `b41e0936`/`8aec4b61` pattern).
// Multiple toasts stack with vertical offset; each auto-dismisses (4s default)
// or persists until manual dismiss (persistent: true).
//
// API:
//   const { toast } = useToast();
//   toast.error("Save failed: ...");
//   toast.warning("Browser doesn't support...", { persistent: true });
//   toast.info("Path copied");
//   toast.success("Settings saved");
//   toast("Generic info"); // callable directly; defaults to info variant

import { createContext, useCallback, useContext, useMemo, useState } from "react";
import Toast from "./Toast";

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((message, opts = {}) => {
    if (!message) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((prev) => [
      ...prev,
      {
        id,
        message: String(message),
        variant: opts.variant || "info",
        persistent: !!opts.persistent,
      },
    ]);
  }, []);

  // Stable callable with variant methods. useMemo so the function identity
  // is stable across renders (consumers can pass it as a dep without churn).
  const toast = useMemo(() => {
    const fn = (message, opts) => showToast(message, opts);
    fn.error = (message, opts = {}) => showToast(message, { ...opts, variant: "error" });
    fn.warning = (message, opts = {}) => showToast(message, { ...opts, variant: "warning" });
    fn.info = (message, opts = {}) => showToast(message, { ...opts, variant: "info" });
    fn.success = (message, opts = {}) => showToast(message, { ...opts, variant: "success" });
    return fn;
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {toasts.map((t, idx) => (
        <Toast
          key={t.id}
          message={t.message}
          variant={t.variant}
          persistent={t.persistent}
          onDismiss={() => dismissToast(t.id)}
          offset={idx * 56}
        />
      ))}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return ctx;
}
